import { Tooltip } from '@chakra-ui/react'
import type { ApplyV2TempBase64File } from '@core/app/types/applyV2Type'
import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2InputCheckbox from '@core/components/ApplyDialogV2/ApplyV2InputCheckbox'
import getAcceptFilesString from '@core/utils/helpers/getAcceptFilesString'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import ApplyV2Switch from '@core/components/ApplyDialogV2/ApplyV2Switch'
import ApplyV2DateSelectGroup from '@core/components/ApplyDialogV2/ApplyV2DateSelectGroup'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import FileUploaderV2 from '@core/components/ApplyDialogV2/FileUploaderV2'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import { usePostCourseFileV2ApplyMutation, usePostCourseV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { isError } from '@sentry/utils'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import React, { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import './ApplyV2CoursesStepAdd.scss'

interface Props {
    className?: string
    onClose?: () => void
}

interface FormikValuesState {
    courseName: string
    courseDescription: string
    issueDate: boolean
    expireDate: boolean
    noExpireDate: boolean
    startDateDay: string
    startDateMonth: string
    startDateYear: string
    endDateDay: string
    endDateMonth: string
    endDateYear: string
    files: ApplyV2TempBase64File[]
}

const baseClass = 'ApplyV2CoursesStepAdd'

function ApplyV2CoursesStepAdd({ className, onClose = () => {} }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
        settingsData,
        navItems,
        storageData,
        postProfileRefetch,
    } = useApplyV2Context()
    const [postFileRequest, { isLoading: isFileLoading, error: fileError, data: fileData }] =
        usePostCourseFileV2ApplyMutation()
    const [postCourseRequest, { isLoading: isCourseLoading, error: courseErrors, data: courseData }] =
        usePostCourseV2ApplyMutation()
    const firstRef = useRef<any>(null)

    useEffect(() => {
        if (firstRef.current) {
            firstRef.current.focus()
        }
    }, [])

    const isExpireDateMandatory = !!settingsData?.corporationSettings?.courseExpiryMandatory
    const validationSchema = Yup.object().shape({
        courseName: Yup.string()
            .trim()
            .required(`${t('5653', 'Required')}`)
            .min(2, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`)
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        courseDescription: Yup.string()
            .trim()
            .required(t('5653', 'Required') ?? '')
            .min(2, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`)
            .max(5000, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 5000 })}`),
        issueDate: Yup.boolean(),
        expireDate: Yup.boolean(),
        noExpireDate: Yup.boolean(),
        startDateDay: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().nullable().required(getTitle('5653', 'Required')),
            otherwise: Yup.string().nullable(),
        }),
        startDateMonth: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().nullable().required(getTitle('5653', 'Required')),
            otherwise: Yup.string().nullable(),
        }),
        startDateYear: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().nullable().required(getTitle('5653', 'Required')),
            otherwise: Yup.string().nullable(),
        }),
        endDateDay: Yup.string().when(['expireDate', 'noExpireDate'], {
            // is: true,
            is: (expireDate: boolean, noExpireDate: boolean) => {
                return expireDate && !noExpireDate
            },
            then: Yup.string()
                .required(getTitle('5653', 'Required'))
                .test(
                    'is-after-start',
                    getTitle('9453', 'End date cannot be earlier than start date'),
                    function (value) {
                        const { startDateYear, startDateMonth, startDateDay, endDateDay, endDateMonth, endDateYear } =
                            this.parent
                        if (!isExpireDateMandatory) return true
                        if (!startDateDay || !startDateMonth || !startDateYear) return true

                        const startDate = dayjs(`${startDateYear}-${startDateMonth}-${startDateDay}`)
                        const endDate = dayjs(`${endDateYear}-${endDateMonth}-${endDateDay}`)

                        return endDate.isAfter(startDate) || endDate.isSame(startDate, 'day')
                    }
                ),
            // .required(t('5653', 'Required') ?? ''),
            otherwise: Yup.string().nullable(),
        }),
        endDateMonth: Yup.string()
            .nullable()
            .when(['expireDate', 'noExpireDate'], {
                is: (expireDate: boolean, noExpireDate: boolean) => {
                    return expireDate && !noExpireDate
                },
                then: Yup.string()
                    .required(getTitle('5653', 'Required'))
                    .test(
                        'is-after-start',
                        getTitle('9453', 'End date cannot be earlier than start date'),
                        function (value) {
                            const {
                                startDateYear,
                                startDateMonth,
                                startDateDay,
                                endDateDay,
                                endDateMonth,
                                endDateYear,
                            } = this.parent
                            if (!isExpireDateMandatory) return true
                            if (!startDateDay || !startDateMonth || !startDateYear) return true

                            const startDate = dayjs(`${startDateYear}-${startDateMonth}-${startDateDay}`)
                            const endDate = dayjs(`${endDateYear}-${endDateMonth}-${endDateDay}`)

                            return endDate.isAfter(startDate) || endDate.isSame(startDate, 'day')
                        }
                    ),
                otherwise: Yup.string().nullable(),
            }),
        endDateYear: Yup.string()
            .nullable()
            .when(['expireDate', 'noExpireDate'], {
                is: (expireDate: boolean, noExpireDate: boolean) => {
                    return expireDate && !noExpireDate
                },
                then: Yup.string()
                    .required(getTitle('5653', 'Required'))
                    .test(
                        'is-after-start',
                        getTitle('9453', 'End date cannot be earlier than start date'),
                        function (value) {
                            const {
                                startDateYear,
                                startDateMonth,
                                startDateDay,
                                endDateDay,
                                endDateMonth,
                                endDateYear,
                            } = this.parent
                            if (!isExpireDateMandatory) return true
                            if (!startDateDay || !startDateMonth || !startDateYear) return true

                            const startDate = dayjs(`${startDateYear}-${startDateMonth}-${startDateDay}`)
                            const endDate = dayjs(`${endDateYear}-${endDateMonth}-${endDateDay}`)

                            return endDate.isAfter(startDate) || endDate.isSame(startDate, 'day')
                        }
                    ),
                otherwise: Yup.string().nullable(),
            }),
        files: Yup.array().max(5),
    })

    const formik = useFormik<FormikValuesState>({
        initialValues: {
            courseName: '',
            courseDescription: '',
            issueDate: false,
            expireDate: isExpireDateMandatory,
            noExpireDate: false,
            startDateDay: '',
            startDateMonth: '',
            startDateYear: '',
            endDateDay: '',
            endDateMonth: '',
            endDateYear: '',
            files: [],
        },
        onSubmit: async (values, { setSubmitting, resetForm, setFieldError, setErrors }) => {
            try {
                const uid = storageData?.postInitUid
                if (!uid) return
                setSubmitting(true)
                const startDate = values.issueDate
                    ? `${values.startDateYear}-${values.startDateMonth}-${values.startDateDay}`
                    : null
                const endDate =
                    values.expireDate &&
                    !values.noExpireDate &&
                    values.endDateYear &&
                    values.endDateMonth &&
                    values.endDateYear
                        ? `${values.endDateYear}-${values.endDateMonth}-${values.endDateDay}`
                        : null

                const uploadSequentially = async (courseUid: string) => {
                    for (const fileUpload of values.files) {
                        try {
                            if ('base64' in fileUpload && typeof fileUpload.base64 === 'string') {
                                const res = await postFileRequest({
                                    uid: uid,
                                    body: {
                                        uid: courseUid,
                                        file: {
                                            name: fileUpload.name,
                                            ext: fileUpload.ext || fileUpload.name.split('.').pop() || '',
                                            base64: fileUpload.base64,
                                        },
                                    },
                                }).unwrap()
                            }
                        } catch (e: any) {
                            formik.setFieldError('files', e?.message || 'error')
                        }
                    }
                }
                const res = await postCourseRequest({
                    uid,
                    body: {
                        courseName: values.courseName,
                        courseDescription: values.courseDescription,
                        startDate,
                        endDate: values.noExpireDate ? null : endDate,
                        courseListId: 0,
                        // files: values.files,
                    },
                }).unwrap()
                const ups = await uploadSequentially(res?.data?.course?.uid)

                postProfileRefetch()
            } catch (error: any) {
                console.error('Submission error:', error)
                if (error?.data?.errors && Array.isArray(error.data.errors)) {
                    const formikErrors: { [key: string]: string } = {}

                    error.data.errors.forEach((err: any) => {
                        switch (err.field) {
                            case 'startDate':
                                formikErrors.startDateYear = err.msg
                                formikErrors.startDateMonth = err.msg
                                formikErrors.startDateDay = err.msg
                                break
                            case 'endDate':
                                formikErrors.endDateYear = err.msg
                                formikErrors.endDateMonth = err.msg
                                formikErrors.endDateDay = err.msg
                                break
                            case 'courseName':
                            case 'courseDescription':
                            case 'files':
                                formikErrors[err.field] = err.msg
                                break
                            default:
                                formikErrors.root = err.msg
                                break
                        }
                    })

                    setErrors(formikErrors)
                }
            } finally {
                setSubmitting(false)
                // console.log('Form submission completed')
            }
        },

        validationSchema,
        // validateOnMount: true,
        // validateOnBlur: true,
        validateOnChange: true,
        enableReinitialize: true,
    })

    return (
        <form className={clsx(baseClass, className)} onSubmit={formik.handleSubmit} noValidate>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__title`}>{getTitle('10388', 'Add course / certificate')}</div>
                <Tooltip
                    hasArrow
                    // isDisabled={isLoading}
                    openDelay={500}
                    label={getTitle('181', 'Close')}
                    // placement={'right'}
                    padding={'8px 16px'}
                    borderRadius={'4px'}
                    backgroundColor={'#19191A'}
                    fontWeight={600}
                    fontSize={'12px'}
                    shouldWrapChildren
                >
                    <ApplyV2Button
                        className={`${baseClass}__header__btn`}
                        size={'small'}
                        variant="transparent"
                        onClick={onClose}
                        leftIcon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                    />
                </Tooltip>
            </div>
            <ApplyV2InputText
                ref={firstRef}
                name="courseName"
                label={getTitle('63', 'Name')}
                value={formik.values.courseName}
                onChange={(e) => {
                    // formik.setFieldTouched('courseName', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseName && formik.errors.courseName}
                // errorMessage={formik.errors.courseName}
                isRequired
            />

            <ApplyV2Switch
                name="issueDate"
                checked={formik.values.issueDate}
                // onChange={formik.handleChange}
                onChange={(e) => {
                    formik
                        .setValues({
                            ...formik.values,
                            issueDate: e.target.checked,
                            // endDateDay: '',
                            // endDateMonth: '',
                            // endDateYear: '',
                            startDateDay: '',
                            startDateMonth: '',
                            startDateYear: '',
                        })
                        .then((l) => {
                            formik.setErrors({ ...l })
                            formik.setTouched({
                                ...formik.touched,
                                // endDateDay: true,
                                // endDateMonth: true,
                                // endDateYear: true,
                                startDateYear: true,
                                startDateMonth: true,
                                startDateDay: true,
                            })
                        })
                }}
                onBlur={formik.handleBlur}
                label={getTitle('7865', 'Issue date')}
            />
            {formik.values.issueDate && (
                <ApplyV2DateSelectGroup
                    // label={getTitle('7577', 'Expiry date')}
                    // isRequired
                    names={{
                        day: 'startDateDay',
                        month: 'startDateMonth',
                        year: 'startDateYear',
                    }}
                    value={{
                        day: formik.values.startDateDay,
                        month: formik.values.startDateMonth,
                        year: formik.values.startDateYear,
                    }}
                    onChange={({ day, month, year }) => {
                        formik.setFieldValue('startDateDay', day)
                        formik.setFieldValue('startDateMonth', month)
                        formik.setFieldValue('startDateYear', year)
                    }}
                    errors={{
                        day: formik.touched.startDateDay && formik.errors.startDateDay,
                        month: formik.touched.startDateMonth && formik.errors.startDateMonth,
                        year: formik.touched.startDateYear && formik.errors.startDateYear,
                    }}
                    onBlur={formik.handleBlur}
                    isDisabled={!formik.values.issueDate}
                    isFutureYears={false}
                    yearsRange={90}
                    isCurrentYear={true}
                />
            )}
            {!isExpireDateMandatory && (
                <ApplyV2Switch
                    name="expireDate"
                    checked={formik.values.expireDate}
                    // onChange={formik.handleChange}
                    onChange={(e) => {
                        formik
                            .setValues({
                                ...formik.values,
                                expireDate: e.target.checked,
                                endDateDay: '',
                                endDateMonth: '',
                                endDateYear: '',
                            })
                            .then((errs) => {
                                formik.setErrors({ ...errs })
                                formik.setTouched({
                                    ...formik.touched,
                                    expireDate: e.target.checked,
                                    endDateDay: true,
                                    endDateMonth: true,
                                    endDateYear: true,
                                })
                            })
                    }}
                    onBlur={formik.handleBlur}
                    label={getTitle('7577', 'Expiry date')}
                />
            )}

            {formik.values.expireDate && (
                <ApplyV2DateSelectGroup
                    label={getTitle('7577', 'Expiry date')}
                    topBar={
                        isExpireDateMandatory && (
                            <ApplyV2InputCheckbox
                                className={`${baseClass}__noExpire`}
                                value={formik.values.noExpireDate}
                                onChange={(e) => {
                                    formik.setFieldValue('noExpireDate', e)
                                    if (e) {
                                        formik
                                            .setValues({
                                                ...formik.values,
                                                noExpireDate: e,
                                                endDateDay: '',
                                                endDateMonth: '',
                                                endDateYear: '',
                                            })
                                            .then(() => {
                                                formik
                                                    .setTouched({
                                                        ...formik.touched,
                                                        endDateDay: undefined,
                                                        endDateMonth: undefined,
                                                        endDateYear: undefined,
                                                    })
                                                    .then(() => {})
                                            })
                                    }
                                }}
                                label={getTitle('9527', 'Does not have an expiry date')}
                                onBlur={formik.handleBlur}
                            />
                        )
                    }
                    // isRequired
                    names={{
                        day: 'endDateDay',
                        month: 'endDateMonth',
                        year: 'endDateYear',
                    }}
                    value={{
                        day: formik.values.endDateDay,
                        month: formik.values.endDateMonth,
                        year: formik.values.endDateYear,
                    }}
                    onChange={({ day, month, year }) => {
                        formik.setFieldValue('endDateDay', day)
                        formik.setFieldValue('endDateMonth', month)
                        formik.setFieldValue('endDateYear', year)
                    }}
                    errors={{
                        day: formik.touched.endDateDay && formik.errors.endDateDay,
                        month: formik.touched.endDateMonth && formik.errors.endDateMonth,
                        year: formik.touched.endDateYear && formik.errors.endDateYear,
                    }}
                    onBlur={formik.handleBlur}
                    isDisabled={!formik.values.expireDate || formik.values.noExpireDate}
                    isRequired={isExpireDateMandatory}
                    isFutureYears={true}
                    yearsRange={10}
                    isCurrentYear={true}
                />
            )}
            <ApplyV2InputTextarea
                name="courseDescription"
                label={getTitle('606', 'Description')}
                value={formik.values.courseDescription}
                onChange={(e) => {
                    // formik.setFieldTouched('courseDescription', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseDescription && formik.errors.courseDescription}
                rows={2}
                isRequired
            />

            <ApplyV2AttachmentsUploader
                className={`${baseClass}__files`}
                label={getTitle('1044', 'Attachments')}
                name="files"
                files={formik.values.files}
                multipleCount={5}
                accept={getAcceptFilesString('ATTACHMENTS')}
                // maxFileSize={1}
                onChange={(files: ApplyV2TempBase64File[]) => {
                    const filesMap = new Map<string, ApplyV2TempBase64File>()
                    formik.values.files?.forEach((file: ApplyV2TempBase64File) => {
                        if (file?.name) {
                            filesMap.set(file.name, file)
                        }
                    })
                    files.forEach((file) => {
                        if (file?.name) {
                            filesMap.set(file.name, file)
                        }
                    })
                    const uniqueFiles = Array.from(filesMap.values())
                    formik.setFieldValue('files', uniqueFiles)
                }}
                onDelete={(file) => {
                    formik.setFieldValue(
                        'files',
                        formik.values.files.filter((f) => f.name !== file.name)
                    )
                }}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.files && JSON.stringify(formik.errors.files)}
            />
            <div className={`${baseClass}__btns`}>
                <ApplyV2Button
                    type={'reset'}
                    variant="transparent"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    // isLoading={formik.isSubmitting}
                    onClick={() => {
                        // console.log(formik.values, formik.touched, formik.errors)
                        formik.resetForm()
                        onClose()
                    }}
                >
                    {getTitle('416', 'Cancel')}
                </ApplyV2Button>
                <ApplyV2Button
                    type={'submit'}
                    // type={'button'}
                    variant="primary"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    isLoading={formik.isSubmitting}
                    isDisabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    // isDisabled={!formik.isValid || !formik.dirty}
                    // onClick={() => {
                    //     console.log('Submit clicked', formik.values)
                    //     console.log('Formik state:', formik.touched)
                    //     console.log('Formik errors:', formik.errors)
                    //     console.log('Formik isValid:', formik.isValid, formik.dirty)
                    // }}
                >
                    {getTitle('179', 'Add')}
                </ApplyV2Button>
            </div>
        </form>
    )
}

export default ApplyV2CoursesStepAdd
